// https://github.com/MADEiN83/react-responsive-hooks
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { create } from 'zustand';

type BreakpointReturnType = {
  isSm: boolean;
  isMd: boolean;
  isLg: boolean;
  isXl: boolean;
  isXxlOutOfRange: boolean;
  isMobile: boolean;
};

type BreakpointTypes = 'sm' | 'md' | 'lg' | 'xl' | 'xxl-out-of-range';

interface IBreakpoint {
  min: number;
  max: number;
  label: BreakpointTypes;
}

const breakpointDefinitions: IBreakpoint[] = [
  {
    min: 0,
    max: 600,
    label: 'sm',
  },
  {
    min: 600,
    max: 960,
    label: 'md',
  },
  {
    min: 960,
    max: 1280,
    label: 'lg',
  },
  {
    min: 1280,
    max: 9999,
    label: 'xl',
  },
  {
    min: 9999,
    max: Number.POSITIVE_INFINITY,
    label: 'xxl-out-of-range',
  },
];

const getViewPortWidthFn = (): number => {
  const { innerWidth = 0 } = window;
  const {
    documentElement: { clientWidth },
  } = document;
  return Math.max(clientWidth, innerWidth);
};

const getViewPortHeightFn = (): number => {
  const { innerHeight = 0 } = window;
  const {
    documentElement: { clientHeight },
  } = document;
  return Math.max(clientHeight, innerHeight);
};

const isOfType = (currentBreakpoint: IBreakpoint, wantedLabel: string): boolean => {
  return currentBreakpoint && currentBreakpoint.label === wantedLabel;
};

export const getBreakPointReturnType = (breakpoint: IBreakpoint): BreakpointReturnType => {
  return {
    isSm: isOfType(breakpoint, 'sm'),
    isMd: isOfType(breakpoint, 'md'),
    isLg: isOfType(breakpoint, 'lg'),
    isXl: isOfType(breakpoint, 'xl'),
    isXxlOutOfRange: isOfType(breakpoint, 'xxl-out-of-range'),
    isMobile: isOfType(breakpoint, 'sm') || isOfType(breakpoint, 'md'),
  };
};

type GetRelatedBreakpointOptions = {
  viewportWidth?: number;
};

export const getRelatedBreakpoint = (options: GetRelatedBreakpointOptions = {}): IBreakpoint => {
  const viewportWidth = options?.viewportWidth || getViewPortWidthFn();

  const foundBreakpoint = breakpointDefinitions.find(
    (def: IBreakpoint) => viewportWidth >= def.min && viewportWidth < def.max
  );

  if (!foundBreakpoint) {
    if (viewportWidth < breakpointDefinitions[0].max) {
      return breakpointDefinitions[0];
    }

    return breakpointDefinitions[breakpointDefinitions.length - 1];
  }

  return foundBreakpoint;
};

type BreakpointStore = {
  viewportWidth: number;
  viewportHeight: number;
  currentBreakpoint: BreakpointReturnType;

  setViewportWidth: (viewportWidth: number) => void;
  onResizeCallback: () => void;
};

export const store = create<BreakpointStore>((set, get) => ({
  viewportWidth: getViewPortWidthFn(),
  viewportHeight: getViewPortHeightFn(),
  currentBreakpoint: getBreakPointReturnType(getRelatedBreakpoint()),

  setViewportWidth: (viewportWidth: number) => set({ viewportWidth }),
  onResizeCallback: () => {
    const viewportWidth = getViewPortWidthFn();

    set({
      viewportWidth: viewportWidth,
      viewportHeight: getViewPortHeightFn(),
      currentBreakpoint: getBreakPointReturnType(getRelatedBreakpoint({ viewportWidth })),
    });
  },
}));

export const getIsMobile = () => store.getState().currentBreakpoint.isMobile;

export const getViewPortWidth = () => store.getState().viewportWidth;
export const useViewportWidth = () => {
  return store((state) => state.viewportWidth);
};

export const getViewPortHeight = () => store.getState().viewportHeight;
export const useViewportHeight = () => {
  return store((state) => state.viewportHeight);
};

export const useBreakpoints = (): BreakpointReturnType => {
  const viewportWidth = useViewportWidth();

  const currentBreakpoint = useMemo(() => {
    return getRelatedBreakpoint({ viewportWidth });
  }, [viewportWidth]);

  const returnValue = useMemo(() => {
    return getBreakPointReturnType(currentBreakpoint);
  }, [currentBreakpoint]);

  return returnValue;
};

let isListenerStarted = false;
export const startViewPortListener = () => {
  if (isListenerStarted) return;
  isListenerStarted = true;

  const onResize = () => {
    store.getState().onResizeCallback();
  };

  const onResizeDebounced = debounce(onResize, 250, { leading: false, trailing: true });

  window.addEventListener('resize', onResizeDebounced);

  window.addEventListener('unload', () => {
    window.removeEventListener('resize', onResizeDebounced);
  });
};
